import axios from 'axios';
import { camelCaseToTitleCase } from '@src/shared/utils/stringFormatters';
import ChatBus from '@shared/components/Chat/ChatBus.js';
import * as SemanticMatchService from '@shared/components/Chat/services/SemanticMatchService.js';
import { onErrorHandler } from '@src/shared/utils/errorHandlers';
import {
  createPricingScenario,
  getPricingScenarios,
  getPricingScenario,
} from '@pe/services/pricingScenarios';
import { filterObject } from '@src/shared/utils/helpers';
import {
  generateLoanTranslationSchema,
  generateBorrowerTranslationSchema,
  generatePropertyTranslationSchema,
  generateSearchTranslationSchema,
  extractOptions,
  filterSchema,
} from '@shared/components/Chat/TranslationUtils.js';
import {
  generateResponse,
  loanTranslationSentiments,
  pricingRetrievalSentiments,
  genericTaskCompleteSentiments,
  followUps,
  apologies,
  pleasantryList,
  formUpdateFailureList,
  tryAgainList,
  fieldIssuesList,
  affirmatives,
  loadedScenarioFailureSentiments,
  ineligibleProductFailureSentiments,
  loadedScenarioSentiments,
  savedScenarioFailureSentiments,
  savedScenarioSentiments,
  celebrations,
  nearMissSuggestedActions,
  explainPricingSuggestedActions,
  getPricingSuggestedActions,
  ruleTestSuggestedActions,
} from '@shared/components/Chat/responseChunks.js';
import {
  ADD_MESSAGE,
  ADD_MESSAGE_TO_HISTORY,
  STOP_AUDIO,
  PLAY_AUDIO,
  SET_LOAN_TRANSLATION_SCHEMA,
  SET_BORROWER_TRANSLATION_SCHEMA,
  SET_PROPERTY_TRANSLATION_SCHEMA,
  SET_SEARCH_TRANSLATION_SCHEMA,
  SET_TEMPLATE,
  SET_CURRENT_PRICING_PARAMETERS,
  SET_PRICING_REQUESTED,
  SET_BEST_PRICES,
  SET_ELIGIBLE_PRODUCTS,
  SET_INELIGIBLE_PRODUCTS,
  SET_PRICING_REQUEST_ID,
  SET_CUSTOM_PARAMETERS,
  COPILOT_GETTERS,
  SET_LATEST_TRAFFIC_COP_CLASSIFICATION,
  SET_SUGGESTED_ACTIONS,
  REMOVE_ACTIVE_REQUEST_CONTROLLER,
  ADD_ACTIVE_REQUEST_CONTROLLER,
  CLEAR_ACTIVE_REQUEST_CONTROLLERS,
  COPILOT_ACTIONS,
  SET_OPTION_MAPS,
  SET_IS_STREAMING,
  SET_STREAMING_MESSAGE,
  SET_CURRENT_MESSAGE_ID,
  REMOVE_CURRENT_MESSAGE_ID,
  SET_RULE_TEST_CONTEXT,
} from '../store/modules/copilot';
import {
  NLI_ASSISTANT,
  NLI_INELIGIBILITY_UI,
  RULE_TESTING_AGENT,
} from '../constants/flags';
import nliApi from '@shared/services/nliApi.js';
const loanScenarioPaths = [
  '/pe/loan-scenario',
  '^/pe/loans/.*$',
  '^/pe/loans-embed.*$',
];
// Wire operators from lxapp's convert_operator; anything unmapped falls through as its symbol.
const FAILED_CONDITION_OPERATOR_WORDS = {
  '==': 'is',
  '!=': 'is not',
  '>': 'greater than',
  '<': 'less than',
  '>=': 'at least',
  '<=': 'at most',
  contains: 'one of',
  excludes: 'not one of',
  between: 'between',
};
export const messageSources = {
  CHAT: 'chat',
  VOICE: 'voice',
  INELIGIBILITYEXPAND: 'ineligibility-expand',
  RULESCENARIOEXPLORER: 'rule-scenario-explorer',
};

export class NliService {
  constructor(store, vueRouter, nextTick) {
    this.store = store;
    this.vueRouter = vueRouter;
    this.nextTick = nextTick;
  }

  copilot(getter) {
    return this.store.getters[`copilot/${getter}`];
  }

  isNLIAssistantActive() {
    return this.store.getters['core/isFlagActive'](NLI_ASSISTANT);
  }

  isNLIIneligibilityUIActive() {
    return this.store.getters['core/isFlagActive'](NLI_INELIGIBILITY_UI);
  }

  isRuleTestingAgentActive() {
    return this.store.getters['core/isFlagActive'](RULE_TESTING_AGENT);
  }

  // This is different than showIneligibilityUI, it can include other nli flags in the future
  isAnyNLIActive() {
    return this.isNLIAssistantActive() || this.isNLIIneligibilityUIActive();
  }

  isUIOnlyMode() {
    return !this.isNLIAssistantActive() && this.isAnyNLIActive();
  }

  showIneligibilityUI() {
    return this.isNLIAssistantActive() || this.isNLIIneligibilityUIActive();
  }

  async trackOrUpateMessage(newMessageText, source = messageSources.CHAT) {
    let trackedMessage = null;
    const currentMessageId = this.copilot(COPILOT_GETTERS.currentMessageId);
    if (!currentMessageId) {
      trackedMessage = await nliApi.trackMessage({
        user_message: newMessageText,
        source: source,
      });
      this.store.commit(
        `copilot/${SET_CURRENT_MESSAGE_ID}`,
        trackedMessage?.message_id,
      );
    } else {
      trackedMessage = await nliApi.updateMessage(currentMessageId, {
        user_message: newMessageText,
        source: source,
      });
    }

    return trackedMessage;
  }

  async initiateCopilotInteraction(
    newMessageText,
    source = messageSources.CHAT,
  ) {
    try {
      if (this.copilot(COPILOT_GETTERS.vocalResponseEnabled)) {
        this.store.dispatch(`copilot/${COPILOT_ACTIONS.initializeIOSAudio}`);
      }

      this.store.commit(`copilot/${SET_SUGGESTED_ACTIONS}`, []);
      await this.addUserMessage(newMessageText, source);
      const trafficCopClassification =
        await this.retrieveTrafficCopActionClassification(newMessageText);
      return await this.routeMessageAction(
        trafficCopClassification,
        newMessageText,
      );
    } catch (error) {
      this.handleNliError(error, 'initiate-copilot-interaction');
    }
  }

  async retrieveTrafficCopActionClassification(newMessageText) {
    try {
      const response = await this.executeNliAction(async options =>
        nliApi.trafficCop(
          {
            prompt: newMessageText,
            history: this.historyToString(
              this.copilot(COPILOT_GETTERS.trafficCopHistoryLength),
            ),
            message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
          },
          options,
        ),
      );
      const trafficCopClassification = response.data.result;
      this.store.commit(
        `copilot/${SET_LATEST_TRAFFIC_COP_CLASSIFICATION}`,
        trafficCopClassification,
      );
      return trafficCopClassification;
    } catch (error) {
      this.handleNliError(error, 'traffic-cop');
    }
  }

  getRandomSuggestedAction(suggestedActionList) {
    return suggestedActionList[
      Math.floor(Math.random() * suggestedActionList.length)
    ];
  }

  determineSuggestedActions(currentPath = this.vueRouter.currentRoute.path) {
    if (!this.isNLIAssistantActive()) {
      return;
    }

    if (!this.pageMatch(loanScenarioPaths, currentPath)) {
      this.store.commit(`copilot/${SET_SUGGESTED_ACTIONS}`, []);
      return;
    }

    const classification = this.copilot(
      COPILOT_GETTERS.latestTrafficCopClassification,
    );

    let suggestedActions = [];

    // Default suggested actions on first interaction
    if (this.copilot(COPILOT_GETTERS.allMessages)?.length == 1) {
      suggestedActions = suggestedActions.concat([
        { category: 'general', text: 'How do we update the scenario?' },
        { category: 'general', text: 'Give me the Polly AI tutorial' },
      ]);

      if (this.copilot(COPILOT_GETTERS.isSaveLoadScenarioAvailable)) {
        suggestedActions.push({ category: 'general', text: 'Load a scenario' });
      }
    }
    if (this.copilot(COPILOT_GETTERS.ineligibleProducts)?.length) {
      const ineligibleProducts = this.copilot(
        COPILOT_GETTERS.ineligibleProducts,
      );
      const randomIndex = Math.floor(Math.random() * ineligibleProducts.length);
      const randomIneligibleProduct = ineligibleProducts[randomIndex];
      suggestedActions = suggestedActions.concat([
        this.getRandomSuggestedAction(nearMissSuggestedActions),
        {
          category: 'ineligibility',
          text: `Why is the ${randomIneligibleProduct.name} ineligible?`,
        },
      ]);
    }
    if (this.copilot(COPILOT_GETTERS.eligibleProducts)?.length) {
      suggestedActions.push(
        this.getRandomSuggestedAction(explainPricingSuggestedActions),
      );
    }
    if (classification === 'json-translation') {
      suggestedActions.push(
        this.getRandomSuggestedAction(getPricingSuggestedActions),
      );
    }

    // Filter out used suggested actions
    const usedSuggestedActionCategories = this.copilot(
      COPILOT_GETTERS.usedSuggestedActionCategories,
    );
    suggestedActions = suggestedActions.filter(
      action => !usedSuggestedActionCategories.includes(action?.category),
    );

    this.store.commit(`copilot/${SET_SUGGESTED_ACTIONS}`, suggestedActions);
    this.scrollToBottom();
  }

  async addUserMessage(newMessageText, source = messageSources.CHAT) {
    this.store.dispatch(`copilot/${COPILOT_ACTIONS.setIsSending}`, true);

    const message = {
      user: true,
      text: newMessageText,
    };
    this.store.commit(`copilot/${ADD_MESSAGE}`, message);
    const messageRecord = {
      content: message.text,
      role: 'user',
      sentAt: new Date(),
    };

    this.scrollToBottom();
    await this.trackOrUpateMessage(newMessageText, source);
    this.store.commit(`copilot/${ADD_MESSAGE_TO_HISTORY}`, messageRecord);
    return message;
  }

  scrollToBottom() {
    this.nextTick(() => {
      // The conversation can render in more than one place at once (the
      // floating widget plus an embedded pane, e.g. the Rule Scenario
      // Explorer modal) — keep every instance pinned to the bottom.
      const messageLists = document.querySelectorAll('.chat-widget__messages');

      if (!messageLists.length) {
        return;
      }

      messageLists.forEach(chatMessages => {
        chatMessages.scrollTop = chatMessages.scrollHeight;
      });
    });
  }

  async routeMessageAction(action, message) {
    try {
      let response = null;

      // Avoid attempting save/load scenario if the user is not allowed to
      if (
        !this.copilot(COPILOT_GETTERS.isSaveLoadScenarioAvailable) &&
        ['load-scenario', 'save-scenario'].includes(action)
      ) {
        return this.handleCopilotResponse(
          await this.mortgageInfoActionHandler(message, [
            'You must explain to the user that you cannot currently manage scenarios.',
          ]),
        );
      }

      switch (action) {
        case 'json-translation':
          response = await this.translateLoanScenarioActionHandler(message);
          break;
        case 'mortgage-question':
          response = await this.mortgageInfoActionHandler(message, [
            'If the question does not relate to the mortgage industry, politely redirect the conversation.',
          ]);
          break;
        case 'get-pricing':
          response = this.getPricingActionHandler();
          break;
        case 'explain-pricing':
          response = await this.searchRateStackActionHandler(message);
          break;
        case 'load-scenario':
          response = await this.loadLoanScenarioActionHandler(message);

          break;
        case 'save-scenario':
          response = await this.saveLoanScenarioActionHandler(message);
          break;
        case 'explain-ineligibility':
          response = await this.explainIneligibilityActionHandler(message);
          break;
        case 'explain-near-miss':
          response = await this.explainNearMissActionHandler(message);
          break;

        default:
          response = await this.mortgageInfoActionHandler(message);
          break;
      }

      await this.handleCopilotResponse(response);
    } catch (error) {
      this.handleNliError(error, action);
    }
  }

  async executeNliAction(apiAction, options = {}) {
    const controller = new AbortController();
    const signal = controller.signal;
    options = {
      timeout: 60000,
      signal: signal,
      ...options,
    };

    this.store.commit(`copilot/${ADD_ACTIVE_REQUEST_CONTROLLER}`, controller);
    const response = await apiAction(options);
    this.store.commit(
      `copilot/${REMOVE_ACTIVE_REQUEST_CONTROLLER}`,
      controller,
    );
    // If the request was aborted, don't process the response
    if (signal.aborted) {
      return;
    }

    return response;
  }

  async handleNliError(error, actionName) {
    if (this.copilot(COPILOT_GETTERS.isSending)) {
      await this.addBotMessage(
        this.simulateStreamResponse(
          "I'm sorry, something went wrong. I've notified the team at Polly. In the meantime, you might try again a different way, or try again later.",
        ),
      );
    }

    this.cleanupCurrentInteraction();
    onErrorHandler(`${actionName} error: ${error}`, 'nli-error', [], true);
  }

  cleanupCurrentInteraction() {
    this.store.commit(`copilot/${REMOVE_CURRENT_MESSAGE_ID}`);
    this.abortActiveControllers();
    this.cleanupStreamingState();
  }

  abortActiveControllers() {
    this.copilot(COPILOT_GETTERS.activeRequestControllers)?.forEach(
      controller => controller.abort(),
    );
    this.store.commit(`copilot/${CLEAR_ACTIVE_REQUEST_CONTROLLERS}`);
  }

  async addBotMessage(responseGenerator) {
    const message = await this.handleStreamGenerator(responseGenerator);

    this.determineSuggestedActions();
    this.streamAudioFromServer(message);
    this.scrollToBottom();

    const messageRecord = {
      content: message.text,
      role: 'assistant',
      sentAt: new Date(),
    };
    this.store.commit(`copilot/${ADD_MESSAGE_TO_HISTORY}`, messageRecord);

    this.cleanupCurrentInteraction();

    return message;
  }

  async summarizeMessage(messageContent) {
    // Only summarize messages of sufficient length, otherwise the
    // summarization is counter productive in that it actually adds tokens instead of reducing
    if (messageContent.length > 100) {
      const response = await this.executeNliAction(async options =>
        nliApi.summarize(
          {
            prompt: messageContent,
            history: null,
            message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
          },
          options,
        ),
      );
      messageContent = response.data.result;
    }
    return messageContent;
  }

  async handleCopilotResponse(response) {
    if (response === null) {
      return;
    }

    // If response is just a string, convert it to a response generator
    const responseGenerator =
      typeof response === 'string'
        ? this.simulateStreamResponse(response)
        : response;

    return await this.addBotMessage(responseGenerator);
  }

  handleStopCopilotResponse() {
    this.abortActiveControllers();
  }

  async translateLoanScenarioActionHandler(message) {
    if (!this.pageMatch(loanScenarioPaths)) {
      return this.wrongPageResponse(
        "If you'd like to update your loan scenario please go to the ",
        '/pe/loan-scenario',
        'Loan Scenario Page',
      );
    }

    let isAllEmpty = true;
    const disabledFields = [];

    const promises = [
      this.executeNliAction(
        async options =>
          nliApi.translateJson(
            {
              translationSchema: JSON.stringify(
                this.copilot(COPILOT_GETTERS.loanTranslationSchema),
                null,
                0,
              ),
              prompt: message,
              history: this.historyToString(
                this.copilot(COPILOT_GETTERS.jsonHistoryLength),
              ),
              message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
            },
            options,
          ),
        {},
      ).then(response => {
        const { isEmpty, newDisabledFields } = this.handleTranslationResponse(
          response,
          this.copilot(COPILOT_GETTERS.template).loan,
          'loan-translation',
        );
        isAllEmpty = isAllEmpty && isEmpty;
        disabledFields.push(...newDisabledFields);
      }),
      this.executeNliAction(
        async options =>
          nliApi.translateJson(
            {
              translationSchema: JSON.stringify(
                this.copilot(COPILOT_GETTERS.borrowerTranslationSchema),
                null,
                0,
              ),
              prompt: message,
              history: this.historyToString(
                this.copilot(COPILOT_GETTERS.jsonHistoryLength),
              ),
              message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
            },
            options,
          ),
        {},
      ).then(response => {
        const { isEmpty, newDisabledFields } = this.handleTranslationResponse(
          response,
          this.copilot(COPILOT_GETTERS.template).borrower,
          'borrower-translation',
        );
        isAllEmpty = isAllEmpty && isEmpty;
        disabledFields.push(...newDisabledFields);
      }),
      this.executeNliAction(
        async options =>
          nliApi.translateJson(
            {
              translationSchema: JSON.stringify(
                this.copilot(COPILOT_GETTERS.propertyTranslationSchema),
                null,
                0,
              ),
              prompt: message,
              history: this.historyToString(
                this.copilot(COPILOT_GETTERS.jsonHistoryLength),
              ),
              message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
            },
            options,
          ),
        {},
      ).then(async response => {
        const { isEmpty, newDisabledFields } = this.handleTranslationResponse(
          response,
          this.copilot(COPILOT_GETTERS.template).property,
          'property-translation',
        );
        isAllEmpty = isAllEmpty && isEmpty;
        disabledFields.push(...newDisabledFields);
      }),
      this.executeNliAction(
        async options =>
          nliApi.translateJson(
            {
              translationSchema: JSON.stringify(
                this.copilot(COPILOT_GETTERS.searchTranslationSchema),
                null,
                0,
              ),
              prompt: message,
              history: this.historyToString(
                this.copilot(COPILOT_GETTERS.jsonHistoryLength),
              ),
              message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
            },
            options,
          ),
        {},
      ).then(response => {
        const { isEmpty, newDisabledFields } = this.handleTranslationResponse(
          response,
          this.copilot(COPILOT_GETTERS.template).criteria,
          'search-translation',
        );
        isAllEmpty = isAllEmpty && isEmpty;
        disabledFields.push(...newDisabledFields);
      }),
    ];

    await Promise.all(promises);

    if (disabledFields.length > 0 && !isAllEmpty) {
      return generateResponse(
        loanTranslationSentiments,
        [
          "Unfortunately I couldn't update the following fields because they are disabled: \n\n- " +
            disabledFields.join('\n- '),
        ],
        null,
        null,
        0.5,
        '.',
        '',
      );
    }

    if (disabledFields.length > 0) {
      return (
        "I'm sorry, I can't update the following fields because they are disabled: \n\n- " +
        disabledFields.join('\n- ')
      );
    }

    // Return special response if all translations are empty
    if (isAllEmpty) {
      return generateResponse(
        formUpdateFailureList,
        tryAgainList,
        apologies,
        pleasantryList,
        0.8,
        '.',
        '.',
      );
    }

    if (this.copilot(COPILOT_GETTERS.autoPricingEnabled)) {
      this.getPricingActionHandler();
      return null;
    }

    return generateResponse([...loanTranslationSentiments], followUps, [
      ...celebrations,
      ...genericTaskCompleteSentiments,
    ]);
  }

  handleTranslationResponse(response, template, translationEvent) {
    let isEmpty = true;
    const { filteredSchema: translation, disabledRemovedKeys } = filterSchema(
      JSON.parse(response.data.result),
      template,
    );
    const newDisabledFields = [
      ...disabledRemovedKeys.map(key => camelCaseToTitleCase(key)),
    ];

    if (Object.keys(translation).length > 0) {
      ChatBus.$emit(translationEvent, translation);
      isEmpty = false;
    }
    return { isEmpty, newDisabledFields };
  }

  async searchRateStackActionHandler(message) {
    if (!this.pageMatch(loanScenarioPaths)) {
      return this.wrongPageResponse(
        "If you'd like to look up rate stack information please go to the ",
        '/pe/loan-scenario',
        'Loan Scenario Page',
      );
    }

    const responseGenerator = await this.streamingResponseGenerator({
      endpoint: `${nliApi.baseApi}/search-rate-stack`,
      data: {
        prompt: message,
        message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
        history: this.historyToString(
          this.copilot(COPILOT_GETTERS.historyLength),
        ),
        pricingRequestId: this.copilot(COPILOT_GETTERS.pricingRequestId),
      },
    });

    return responseGenerator;
  }

  async executeSemanticMatchTournament(
    productRates,
    recalibratedPrompt,
    selectedColumns,
  ) {
    let selectedRates = productRates;
    while (selectedRates.length > 15) {
      SemanticMatchService.shuffleArray(selectedRates);
      const groups = SemanticMatchService.groupRates(selectedRates);
      const matchCount = SemanticMatchService.determineMatchCount(groups);

      // Make match selections in parallel
      const promises = groups.map(group => {
        group = SemanticMatchService.sortRatesByRate(group);
        return this.executeNliAction(async options =>
          nliApi.semanticMatch(
            {
              prompt: recalibratedPrompt,
              options: SemanticMatchService.formatRateStackSearchResultsString(
                group,
                selectedColumns,
              ),
              match_count: matchCount,
              message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
            },
            options,
          ),
        );
      });

      const selectionResults = await Promise.all(promises);

      selectedRates = SemanticMatchService.getRatesFromRateIds(
        selectionResults?.map(result => result?.data?.result) || [],
        productRates,
      );
    }

    return selectedRates;
  }

  async selectColumns(message) {
    const columnSelectionResponse = await this.executeNliAction(async options =>
      nliApi.selectColumns(
        {
          prompt: message,
          message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
        },
        options,
      ),
    );

    return JSON.parse(columnSelectionResponse.data.result);
  }

  async normalizeAndTag(message, glossary) {
    const normalizationResponse = await this.executeNliAction(async options =>
      nliApi.normalizeAndTag(
        {
          prompt: message,
          glossary: glossary,
          message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
        },
        options,
      ),
    );

    return normalizationResponse.data.result;
  }

  /**
   * Handles mortgage-related questions by contextualizing the message and streaming responses from the backend.
   *
   * @param {string} message - User's mortgage question or prompt
   * @param {Array} additionalRules - Custom rules to override defaults defined in the backend
   * @param {string|null} model - Specific AI model to use, defaults to configured mortgage info model
   * @param {number} historyLength - Number of recent messages to include for context
   * @returns {Promise<Object>} Streaming response generator for real-time mortgage information
   *
   * @example
   * // Use backend defaults for general mortgage questions
   * await mortgageInfoActionHandler("What is PMI?");
   *
   * // Override defaults for specific scenarios
   * await mortgageInfoActionHandler("Explain rates", ["Be brief"]);
   */
  async mortgageInfoActionHandler(
    message,
    additionalRules = null,
    model = null,
    historyLength = this.copilot(COPILOT_GETTERS.historyLength),
  ) {
    const contextualizationResponse = await this.executeNliAction(
      async options =>
        nliApi.contextualize(
          {
            prompt: message,
            history: this.historyToString(historyLength),
            message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
          },
          options,
        ),
    );

    const data = {
      prompt: message,
      rag_prompt: contextualizationResponse.data.result,
      additional_rules: additionalRules,
      model: model,
      history: this.historyToString(historyLength),
      ui_source: 'old_ui',
      message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
    };
    const ruleTestContext = this.copilot(COPILOT_GETTERS.ruleTestContext);
    if (ruleTestContext?.agentContext) {
      data.page_data_context = ruleTestContext.agentContext;
    }

    const responseGenerator = await this.streamingResponseGenerator({
      endpoint: `${nliApi.baseApi}/mortgage-info`,
      data,
    });

    return responseGenerator;
  }

  async loadLoanScenarioActionHandler(message) {
    if (this.pageMatch(loanScenarioPaths)) {
      const response = await getPricingScenarios(
        this.store.state.organizations.organization.id,
        this.copilot(COPILOT_GETTERS.scenarioRetrievalOptions),
      );
      const scenarioMap = response.results.map(result => ({
        name: result.scenario.name,
        id: result.scenario.id,
      }));

      const optionResponse = await this.executeNliAction(async options =>
        nliApi.optionSelection(
          {
            prompt: message,
            options: JSON.stringify(scenarioMap),
            message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
          },
          options,
        ),
      );

      if (optionResponse.status && optionResponse.data.result >= 1) {
        const selectedId = optionResponse.data.result;

        let pe3Request = null;
        try {
          const detail = await getPricingScenario(
            this.store.state.organizations.organization.id,
            selectedId,
          );
          pe3Request = detail?.data?.pe3_custom_request;
        } catch (error) {
          this.handleNliError(error, 'nli-select-option-pricing-scenario');
        }

        if (!pe3Request) {
          return generateResponse(
            [...loadedScenarioFailureSentiments],
            tryAgainList,
            apologies,
            null,
            0.8,
            '.',
            '.',
          );
        }

        this.store.dispatch('pricing/mergeScenarioWithTemplate', {
          scenario: pe3Request,
          customParameters: this.copilot(COPILOT_GETTERS.customParameters),
        });
        ChatBus.$emit('load-scenario');

        return generateResponse(
          [...loadedScenarioSentiments, ...genericTaskCompleteSentiments],
          followUps,
          celebrations,
        );
      }
      // Failed to load scenario
      return generateResponse(
        [...loadedScenarioFailureSentiments],
        tryAgainList,
        apologies,
        null,
        0.8,
        '.',
        '.',
      );
    }

    return this.wrongPageResponse(
      "If you'd like to load a loan scenario please go to the ",
      '/pe/loan-scenario',
      'Loan Scenario Page',
    );
  }

  async saveLoanScenarioActionHandler(message) {
    if (this.pageMatch(loanScenarioPaths)) {
      const scenarioNameResponse = await this.executeNliAction(async options =>
        nliApi.nameExtraction(
          {
            prompt: message,
            additional_rules: [
              'Extract the name of the scenario. (ex: {message: Save this scenario as investment property, name: investment property)',
            ],
            message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
          },
          options,
        ),
      );

      if (
        scenarioNameResponse.status &&
        scenarioNameResponse.data.result.toLowerCase() !== 'none'
      ) {
        await createPricingScenario(
          this.store.state.organizations.organization.id,
          scenarioNameResponse.data.result,
          this.store.state.pricing.pricingTemplate,
          this.copilot(COPILOT_GETTERS.customParameters),
        );

        return generateResponse(
          [...savedScenarioSentiments, ...genericTaskCompleteSentiments],
          followUps,
        );
      }
      // Failed to load scenario
      return generateResponse(
        [...savedScenarioFailureSentiments],
        tryAgainList,
        apologies,
        null,
        0.8,
        '.',
        '.',
      );
    }

    return this.wrongPageResponse(
      "If you'd like to save a loan scenario please go to the ",
      '/pe/loan-scenario',
      'Loan Scenario Page',
    );
  }

  async explainIneligibilityActionHandler(message) {
    if (!this.pageMatch(loanScenarioPaths)) {
      return this.wrongPageResponse(
        "If you'd like to interact with products and pricing please go to the ",
        '/pe/loan-scenario',
        'Loan Scenario Page',
      );
    }

    if (!this.copilot(COPILOT_GETTERS.ineligibleProducts)) {
      return generateResponse(
        ["I don't see any ineligible products"],
        ['Please re-run pricing and ask again'],
        apologies,
        null,
        0.9,
        '.',
        '.',
      );
    }

    const optionResponse = await this.executeNliAction(async options =>
      nliApi.optionSelection(
        {
          prompt: message,
          options: JSON.stringify(
            this.copilot(COPILOT_GETTERS.ineligibleProducts).map(product => ({
              name: product.name,
              id: product.id,
            })),
          ),
          message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
        },
        options,
      ),
    );

    const trimmedOptionId = optionResponse.data.result.replace(/"/g, '');
    const selectedProduct = this.copilot(
      COPILOT_GETTERS.ineligibleProducts,
    ).find(product => {
      return product.id == trimmedOptionId;
    });
    if (optionResponse.status && trimmedOptionId != -1 && selectedProduct) {
      return await this.generateIneligibilityExplanation(
        message,
        selectedProduct,
      );
    }

    // Failed to find ineligible product by name
    return generateResponse(
      [...ineligibleProductFailureSentiments],
      tryAgainList,
      apologies,
      null,
      0.8,
      '.',
      '.',
    );
  }

  async getAudienceParamOptions() {
    let audienceOptions = this.store.getters['pricing/audienceOptions'];

    if (!audienceOptions || audienceOptions.length === 0) {
      try {
        const versionId =
          this.store.getters['pricingVersionDetails/terseConfiguration']?.id;
        await this.store.dispatch(
          'pricing/fetchAudiencesForVersion',
          versionId,
        );
        audienceOptions = this.store.getters['pricing/audienceOptions'];
      } catch (error) {
        this.handleNliError(error, 'nli-get-audience-options');
      }
    }

    return audienceOptions || [];
  }

  async generateParamTranslationMap() {
    const audienceParamOptions = await this.getAudienceParamOptions();

    return {
      AudienceId: audienceParamOptions,
    };
  }

  async generateIneligibilityExplanation(message, ineligibleProduct) {
    const selectedProductId = ineligibleProduct['id'];
    const scenarioAnalysisRequest = {
      pricingRequestId: this.copilot(COPILOT_GETTERS.pricingRequestId),
      ineligibleProductId: selectedProductId,
      paramTranslationMap: await this.generateParamTranslationMap(),
      currentOptions: this.copilot(COPILOT_GETTERS.optionMaps),
    };
    const loanScenarioAnalysis = await nliApi.scenarioAnalysis(
      scenarioAnalysisRequest,
    );

    const ineligibleProductAnalysis =
      loanScenarioAnalysis.data.ineligibleProducts[0];
    const failedConditions = ineligibleProductAnalysis.failedConditions;

    if (
      !failedConditions?.length &&
      ineligibleProductAnalysis.solutions.length === 0
    ) {
      return (
        "Oops! It seems like I've hit a snag analyzing this rule. I'm really sorry for the " +
        "inconvenience. Rest assured, I've already flagged this for further review. Our team is " +
        "always working on improvements and we'll make sure to enhance our ability to handle " +
        'situations like this in the future. Thank you for your patience and understanding.'
      );
    }

    // only take the first solution for now, handle multiple later
    const ineligibilitySolution = ineligibleProductAnalysis.solutions[0];
    // The near-miss solution is only a fallback: it lists fixes, not the conditions that fired.
    const additionalContext = failedConditions?.length
      ? `${
          ineligibleProduct.name
        } Failed Conditions:\n${this.parseFailedConditions(failedConditions)}`
      : `${
          ineligibleProduct.name
        } Failed Requirements: \n${this.parseSolutionRequirements(
          ineligibilitySolution,
        )}`;

    const responseGenerator = await this.streamingResponseGenerator({
      endpoint: `${nliApi.baseApi}/explain-ineligibility`,
      data: {
        prompt: message,
        additional_context: additionalContext,
        message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
      },
    });

    return responseGenerator;
  }

  async handleUiIneligibilityInteraction(
    ineligibleProduct,
    source = messageSources.INELIGIBILITYEXPAND,
  ) {
    const newMessageText = `Why is the ${ineligibleProduct.name} product ineligible?`;
    this.addUserMessage(newMessageText, source);

    const ineligibilityExplanation =
      await this.generateIneligibilityExplanation(
        newMessageText,
        ineligibleProduct,
      );

    return await this.handleCopilotResponse(ineligibilityExplanation);
  }

  async runRuleTestForRule({ configId, ruleId, ruleName }) {
    try {
      await this.addUserMessage(
        `Generate test scenarios for rule "${ruleName}"`,
        messageSources.RULESCENARIOEXPLORER,
      );
      const response = await this.executeNliAction(async options =>
        nliApi.ruleTest(
          {
            rule_id: ruleId,
            changeset_id: configId,
            message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
          },
          options,
        ),
      );
      const payload = response?.data;
      if (!payload || payload.status !== 'ok') {
        throw payload || new Error('rule test failed');
      }
      return payload;
    } catch (error) {
      this.cleanupCurrentInteraction();
      throw error;
    }
  }

  publishRuleTestReport(payload, ruleId) {
    const chatBody =
      `${payload.markdown}\n\n` +
      'You can explore these test scenarios right here — ask Polly AI a ' +
      'question, or pick one of the suggestions below.';
    // The report is locally known and complete, so it is appended directly
    // rather than through the typewriter stream: the stream's setTimeout
    // pacing is throttled to ~1s/tick in unfocused windows, and while it
    // runs it holds the chat's single streaming slot open for any
    // concurrent interaction to garble. The /nli/rule-test endpoint has
    // already recorded the response on the tracked NLIMessage row.
    this.store.commit(`copilot/${ADD_MESSAGE}`, {
      text: chatBody,
      sender: this.copilot(COPILOT_GETTERS.botName),
      reaction: 0,
      id: this.copilot(COPILOT_GETTERS.currentMessageId),
      streaming: false,
    });
    this.store.commit(`copilot/${ADD_MESSAGE_TO_HISTORY}`, {
      role: 'assistant',
      content: chatBody,
      sentAt: new Date(),
    });
    this.scrollToBottom();
    this.cleanupCurrentInteraction();
    this.store.commit(`copilot/${SET_SUGGESTED_ACTIONS}`, [
      ...ruleTestSuggestedActions,
    ]);
    this.store.commit(`copilot/${SET_RULE_TEST_CONTEXT}`, {
      ruleId,
      agentContext: payload.agent_context || '',
      summaryMarkdown: payload.markdown,
    });
  }

  async explainNearMissActionHandler(message) {
    if (!this.pageMatch(loanScenarioPaths)) {
      return this.wrongPageResponse(
        "If you'd like to interact with products and pricing please go to the ",
        '/pe/loan-scenario',
        'Loan Scenario Page',
      );
    }
    if (
      !this.copilot(COPILOT_GETTERS.ineligibleProducts)?.length &&
      !this.copilot(COPILOT_GETTERS.eligibleProducts)?.length
    ) {
      return generateResponse(
        ["I don't see any products"],
        ['Please re-run pricing and ask again'],
        apologies,
        null,
        0.9,
        '.',
        '.',
      );
    }

    const nearMissResults = await nliApi.nearMiss({
      pricingRequestId: this.copilot(COPILOT_GETTERS.pricingRequestId),
      productIds: [],
      currentOptions: this.copilot(COPILOT_GETTERS.optionMaps),
      paramTranslationMap: await this.generateParamTranslationMap(),
    });
    const nearMissProducts = [
      ...(nearMissResults.data.eligibleProducts || []),
      ...(nearMissResults.data.ineligibleProducts || []),
    ];
    const nearMissContext = this.parseNearMissContext(nearMissProducts);
    if (nearMissContext.length == 0) {
      return await this.mortgageInfoActionHandler(message, [
        "Explain to the user that you're unable to find any additional products that could easily be made eligible right now.",
        'Do not make any additional offers or suggestions.',
      ]);
    }

    const nearMissRequirementsList = await Promise.all(
      nearMissContext.map(async product => {
        const response = await nliApi.nearMissRequirementList({
          prompt: message,
          additional_context: product,
          message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
        });
        return response.data.result;
      }),
    );

    const nearMissProductRequirements = nearMissRequirementsList.map(
      productRequirements => {
        return JSON.parse(productRequirements);
      },
    );

    const responseGenerator = await this.streamingResponseGenerator({
      endpoint: `${nliApi.baseApi}/explain-near-miss`,
      data: {
        prompt: message,
        additional_context: JSON.stringify(nearMissProductRequirements),
        message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
      },
    });

    return responseGenerator;
  }

  explainPricing(isPricingRequest = false) {
    if (this.copilot(COPILOT_GETTERS.bestPrices)) {
      this.addBotMessage(
        this.generatePricingSuccessResponse(
          this.copilot(COPILOT_GETTERS.bestPrices),
          isPricingRequest,
        ),
      );
    } else {
      this.addBotMessage(
        generateResponse(
          ["I don't see any pricing info"],
          ['Please re-run pricing and ask again'],
          apologies,
          null,
          0.9,
          '.',
          '.',
        ),
      );
    }
  }

  determineDirectionFromDiff(diff) {
    if (diff < 0) {
      return 'lower';
    } else if (diff > 0) {
      return 'higher';
    } else {
      return 'same';
    }
  }

  parseNearMissContext(nearMissProducts) {
    const parsedProducts = [];
    let eligibilityContext = '';
    let impactContext = '';
    for (const product of nearMissProducts) {
      let result = '';
      result += result ? '\n\n' : '';
      const solution = product?.solutions?.[0];
      if (!solution) continue;
      const units = product.impact.units;

      if (product.isEligible) {
        eligibilityContext = 'is currently eligible but could have';
      } else {
        eligibilityContext =
          'is currently ineligible but could be made eligible with';
      }

      if (product.impact.difference) {
        const rateDiff = product.impact.rate;
        const priceDiff = product.impact.netPrice;
        const rateDirection = this.determineDirectionFromDiff(rateDiff);
        const priceDirection = this.determineDirectionFromDiff(priceDiff);
        impactContext = `${Math.abs(
          rateDiff,
        )} ${units} ${rateDirection} rate and ${Math.abs(
          priceDiff,
        )} ${units} ${priceDirection} price by addressing the Failed Requirements using the provided Resolution Values below:\n`;
      } else {
        impactContext = `a PAR rate of ${product.impact.rate}% and a final price of ${product.impact.netPrice} by addressing the Failed Requirements using the provided Resolution Values below:\n`;
      }
      result += `Product '${product.name}' ${eligibilityContext} ${impactContext}`;

      Object.entries(solution).forEach(([parameter, details], i) => {
        if (!details?.solutionRequirement) return;
        result += `${i + 1}. ${parameter} ${this.determineSolutionRequirement(
          details.solutionRequirement,
        )} `;
        result += `(failed because ${parameter} is currently ${details.solutionRequirement.parameterValue}). Resolution Value: ${details.resolutionValue}\n`;
      });

      if (result.length) parsedProducts.push(result);
    }

    return parsedProducts;
  }

  generatePricingSuccessResponse(minPrices, isPricingRequest = false) {
    const minPricesList = Object.entries(minPrices).map(
      ([key, value]) =>
        `- ${key}: ${value.price.rate}% rate on the ${value.product.name} product`,
    );
    const bestPriceStatement =
      minPricesList.length > 1
        ? 'The lowest rate par prices...'
        : 'The lowest rate par price...';

    const followUp = minPricesList.length
      ? ['\n\n' + bestPriceStatement + ` \n\n${minPricesList.join('\n')}`]
      : followUps;

    const eligibleSentiments = isPricingRequest
      ? [...pricingRetrievalSentiments, ...genericTaskCompleteSentiments]
      : affirmatives;

    const sentiments = minPricesList.length
      ? eligibleSentiments
      : ['It looks like there are no eligible products'];

    const followUpEndChar = minPricesList.length ? '.' : '?';

    return generateResponse(
      sentiments,
      followUp,
      null,
      null,
      null,
      '.',
      followUpEndChar,
    );
  }

  getPricingActionHandler() {
    if (this.pageMatch(loanScenarioPaths)) {
      this.store.commit(`copilot/${SET_PRICING_REQUESTED}`, true);
      ChatBus.$emit('get-pricing');

      return null;
    }

    return this.wrongPageResponse(
      "If you'd like to get pricing please go to the ",
      '/pe/loan-scenario',
      'Loan Scenario Page',
    );
  }

  generateSchemas(
    template = {},
    customParameters = {},
    currentPricingParameters = {},
  ) {
    // Call your functions to generate the schemas here
    const loanCustomParameters = filterObject(
      customParameters,
      param => param?.valueCategory?.toLowerCase() === 'loan',
    );
    this.store.commit(
      `copilot/${SET_LOAN_TRANSLATION_SCHEMA}`,
      generateLoanTranslationSchema(
        template,
        loanCustomParameters,
        currentPricingParameters?.loan || {},
      ),
    );

    const borrowerCustomParameters = filterObject(customParameters, param =>
      ['borrower', 'custom'].includes(param?.valueCategory?.toLowerCase()),
    );
    this.store.commit(
      `copilot/${SET_BORROWER_TRANSLATION_SCHEMA}`,
      generateBorrowerTranslationSchema(
        template,
        borrowerCustomParameters,
        currentPricingParameters?.borrower || {},
      ),
    );

    const propertyCustomParameters = filterObject(
      customParameters,
      param => param?.valueCategory?.toLowerCase() === 'property',
    );
    this.store.commit(
      `copilot/${SET_PROPERTY_TRANSLATION_SCHEMA}`,
      generatePropertyTranslationSchema(
        template,
        propertyCustomParameters,
        currentPricingParameters?.property || {},
      ),
    );

    this.store.commit(
      `copilot/${SET_SEARCH_TRANSLATION_SCHEMA}`,
      generateSearchTranslationSchema(
        template,
        currentPricingParameters?.criteria || {},
      ),
    );

    this.store.commit(`copilot/${SET_OPTION_MAPS}`, {
      ...extractOptions(this.copilot(COPILOT_GETTERS.loanTranslationSchema)),
      ...extractOptions(
        this.copilot(COPILOT_GETTERS.borrowerTranslationSchema),
      ),
      ...extractOptions(
        this.copilot(COPILOT_GETTERS.propertyTranslationSchema),
      ),
      ...extractOptions(this.copilot(COPILOT_GETTERS.searchTranslationSchema)),
    });
  }

  pageMatch(paths, currentPath = this.vueRouter.currentRoute.path) {
    return paths.some(path => new RegExp(path).test(currentPath));
  }

  wrongPageResponse(clarification = null, link = null, linkText = null) {
    return this.simulateStreamResponse(
      `I'm sorry, I can't do that here. ${clarification}`,
      link,
      linkText,
    );
  }

  async sendAudioMessage(audioBase64, source = messageSources.VOICE) {
    await this.trackOrUpateMessage('audio', source);
    nliApi
      .transcribeAudio(
        audioBase64,
        this.copilot(COPILOT_GETTERS.currentMessageId),
      )
      .then(response => {
        if (response.status) {
          this.initiateCopilotInteraction(response.data.result, source);
        } else {
          this.handleNliError(
            `Error during audio transcription: ${response.data.results}`,
            'audio-transcription',
          );
        }
      })
      .catch(error => {
        this.handleNliError(
          `Error during audio transcription: ${error}`,
          'audio-transcription',
        );
      });
  }

  streamAudioFromServer(message) {
    if (!this.copilot(COPILOT_GETTERS.vocalResponseEnabled)) {
      return;
    }
    // Stop currently playing audio
    this.store.commit(`copilot/${STOP_AUDIO}`);

    // Create an axios request to send the data
    axios
      .post(
        '/nli/tts',
        {
          prompt: message.text,
          message_id: this.copilot(COPILOT_GETTERS.currentMessageId),
        },
        {
          headers: { 'Content-Type': 'application/json' },
          responseType: 'arraybuffer',
        },
      )
      .then(response => {
        // Create a new Blob from the response data
        const audioBlob = new Blob([response.data], {
          type: 'audio/mpeg',
        });

        // Create URL from the Blob
        const audioUrl = URL.createObjectURL(audioBlob);
        this.store.commit(`copilot/${PLAY_AUDIO}`, audioUrl);
      })
      .catch(error =>
        this.handleNliError(
          `Error Streaming Audio: ${error}`,
          'audio-streaming',
        ),
      );
  }

  /**
   * Render the conditions that made each disqualifying rule fire as plain lines the
   * ineligibility prompt can reason over. Empty input renders as an empty string.
   */
  parseFailedConditions(failedConditions) {
    return failedConditions
      .map(rule => {
        const header = rule.conditionsHold
          ? `Rule "${rule.ruleName}" disqualifies the product because all of the following are true:`
          : `Rule "${rule.ruleName}" disqualifies the product because these requirements are not met:`;
        const conditionLines = rule.conditions.map(condition => {
          const operatorWords =
            FAILED_CONDITION_OPERATOR_WORDS[condition.operator] ??
            condition.operator;
          return `- ${condition.parameter} ${operatorWords} ${condition.requirement} (currently ${condition.parameterValue})`;
        });

        return [header, ...conditionLines].join('\n');
      })
      .join('\n\n');
  }

  parseSolutionRequirements(solutionRequirements) {
    let result = ''; // Initialize the result string

    Object.entries(solutionRequirements.parameters).forEach(
      ([parameter, details], i) => {
        result += `${i + 1}. ${parameter} ${this.determineSolutionRequirement(
          details,
        )} `;
        result += `(failed because ${parameter} is currently ${details.parameterValue})\n\n`;
      },
    );

    return result;
  }

  determineSolutionRequirement(solutionRequirement) {
    const requirements = solutionRequirement.operators;
    const requirementStrings = Object.entries(requirements).map(
      ([operator, requirmentData]) => {
        // Spread operator so we can mutate without modifying the original requirements
        const optionsArray = requirmentData?.example_options?.length
          ? [...requirmentData.example_options]
          : [...requirmentData.requirements];
        if (
          requirmentData.options_count &&
          requirmentData?.example_options?.length < requirmentData.options_count
        ) {
          optionsArray.push(
            'and ' +
              (requirmentData.options_count -
                requirmentData.example_options.length) +
              ' more',
          );
        }
        return `${this.determineSolutionRequirementDirective(
          operator,
          requirmentData,
        )} (${optionsArray.join(', ')})`;
      },
    );

    return `${requirementStrings.join(' and ')}`;
  }

  determineSolutionRequirementDirective(operator, requirementData) {
    let directive = 'must be';

    // In the case of contains when a rule causes ineligibility, or excludes when a rule allows eligibility
    if (operator == '!=') {
      directive = 'cannot be';
    }

    // This means we are doing a numeric comparison, in which case we need to include the operator for context
    if (!['==', '!='].includes(operator)) {
      directive = directive += ` ${operator}`;
    }

    // We add this language to make multi requirement explanations like contains/excludes more clear
    if (requirementData?.example_options?.length > 1) {
      directive = directive += ' any of these:';
    }
    if (
      requirementData?.example_options?.length != requirementData?.options_count
    ) {
      directive = directive.replace('any of these:', 'any option like these:');
    }

    return directive;
  }

  parseFieldErrors(errors) {
    const fields = errors
      .map(error => error.split(' ')[0])
      .map(error => error.split('.')[1]);
    const fieldDisplayNames = fields.map(field => {
      if (field.includes('countyFipsCode')) {
        field = field.replace('countyFipsCode', 'county');
      }
      field = camelCaseToTitleCase(field);

      return field;
    });

    const bulletList = fieldDisplayNames.map(field => `- ${field}`).join('\n');

    return bulletList;
  }

  historyToString(
    mostRecentNumber = this.copilot(COPILOT_GETTERS.historyLength),
  ) {
    const trimmedHistory = this.trimHistoryToMostRecentAssistant(
      this.copilot(COPILOT_GETTERS.currentHistory),
    );
    const messages =
      mostRecentNumber === 0
        ? []
        : trimmedHistory
            .slice(-mostRecentNumber)
            .map(
              message =>
                '(' +
                message.sentAt.toLocaleString() +
                ') ' +
                message.role +
                ': ' +
                message.content,
            );
    return messages.join('\n');
  }

  // This method trims the "current" user message from the history if it exists
  trimHistoryToMostRecentAssistant(
    history = this.copilot(COPILOT_GETTERS.currentHistory),
  ) {
    // Reverse the messages, so they start from the end
    const reversedHistory = [...history].reverse();
    // Find the index of the first Assistant message
    const assistantMessageIndex = reversedHistory.findIndex(
      message => message.role === 'assistant',
    );

    // If an assistant message is found, splice out the messages before it
    if (assistantMessageIndex !== -1) {
      reversedHistory.splice(0, assistantMessageIndex);
    }

    return reversedHistory.reverse();
  }

  onLoanTemplateUpdated(template) {
    this.store.commit(`copilot/${SET_TEMPLATE}`, template);
    this.generateSchemas(
      template,
      this.copilot(COPILOT_GETTERS.customParameters),
      this.copilot(COPILOT_GETTERS.currentPricingParameters),
    );
  }

  onPricingParametersUpdated(currentPricingParameters) {
    this.store.commit(
      `copilot/${SET_CURRENT_PRICING_PARAMETERS}`,
      currentPricingParameters,
    );
    this.generateSchemas(
      this.copilot(COPILOT_GETTERS.template),
      this.copilot(COPILOT_GETTERS.customParameters),
      currentPricingParameters,
    );
  }

  onPricingError(errors) {
    const bulletList = this.parseFieldErrors(errors);

    this.addBotMessage(
      generateResponse(
        fieldIssuesList,
        ['\n'.concat(bulletList)],
        apologies,
        null,
        0.5,
        ':',
      ),
    );
    this.store.commit(`copilot/${SET_PRICING_REQUESTED}`, false);
  }

  onPricingSuccess(pricingData, pricingRequestId) {
    this.store.commit(`copilot/${SET_BEST_PRICES}`, pricingData.minPrices);
    this.store.commit(
      `copilot/${SET_ELIGIBLE_PRODUCTS}`,
      pricingData.eligibleProducts,
    );
    this.store.commit(
      `copilot/${SET_INELIGIBLE_PRODUCTS}`,
      pricingData.ineligibleProducts,
    );
    this.store.commit(`copilot/${SET_PRICING_REQUEST_ID}`, pricingRequestId);

    if (this.copilot(COPILOT_GETTERS.pricingRequested)) {
      this.explainPricing(true);
    }
    this.store.commit(`copilot/${SET_PRICING_REQUESTED}`, false);
    this.determineSuggestedActions();
  }

  onCustomParametersUpdated(customParameters) {
    this.store.commit(
      `copilot/${SET_CUSTOM_PARAMETERS}`,
      customParameters || {},
    );
    this.generateSchemas(
      this.copilot(COPILOT_GETTERS.template),
      customParameters,
      this.copilot(COPILOT_GETTERS.currentPricingParameters),
    );
  }

  async handleStreamGenerator(responseGenerator) {
    this.initializeStreamingState();
    const { stream, link, linkText, controller } = responseGenerator;
    const { finalContent, response } = await this.processStreamContent(
      stream,
      controller,
    );

    if (this.copilot(COPILOT_GETTERS.currentMessageId)) {
      nliApi.updateMessage(this.copilot(COPILOT_GETTERS.currentMessageId), {
        response: finalContent,
      });
    }

    const message = {
      text: finalContent,
      sender: this.copilot(COPILOT_GETTERS.botName),
      reaction: 0,
      id: this.copilot(COPILOT_GETTERS.currentMessageId),
      streaming: false,
      link: link,
      linkText: linkText,
      citations: response?.citations || [],
    };

    this.store.commit(`copilot/${SET_STREAMING_MESSAGE}`, message);
    this.cleanupStreamingState();

    return message;
  }

  initializeStreamingState() {
    this.store.commit(`copilot/${SET_IS_STREAMING}`, true);
    this.store.commit(`copilot/${ADD_MESSAGE}`, {
      streaming: true,
      sender: this.copilot(COPILOT_GETTERS.botName),
      text: '',
      reaction: 0,
    });
  }

  async processStreamContent(stream, abortController = undefined) {
    let accumulatedContent = '';
    let response = null;
    let lastUpdateTime = Date.now();
    const MIN_UPDATE_INTERVAL = 30;
    const CHUNK_SIZE = 2;

    for await (const chunk of stream) {
      if (abortController?.signal?.aborted) {
        return { finalContent: accumulatedContent, response: null };
      }
      if (chunk.content) {
        accumulatedContent = await this.processChunk(
          chunk.content,
          accumulatedContent,
          lastUpdateTime,
          MIN_UPDATE_INTERVAL,
          CHUNK_SIZE,
        );
        lastUpdateTime = Date.now();
      }
      if (chunk.done) {
        response = chunk.response;
        break;
      }
    }

    return { finalContent: accumulatedContent, response };
  }

  async processChunk(
    content,
    accumulatedContent,
    lastUpdateTime,
    minInterval,
    chunkSize,
  ) {
    try {
      let currentIndex = 0;

      while (currentIndex < content.length) {
        const now = Date.now();
        if (now - lastUpdateTime >= minInterval) {
          const nextIndex = Math.min(currentIndex + chunkSize, content.length);
          const currentChunk = content.substring(currentIndex, nextIndex);
          accumulatedContent += currentChunk;

          this.store.commit(`copilot/${SET_STREAMING_MESSAGE}`, {
            text: accumulatedContent,
          });
          this.scrollToBottom();

          currentIndex = nextIndex;
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }
    } catch (error) {
      console.warn('Error processing chunk content:', error);
    }

    return accumulatedContent;
  }

  cleanupStreamingState() {
    this.store.commit(`copilot/${SET_IS_STREAMING}`, false);
    this.store.dispatch(`copilot/${COPILOT_ACTIONS.setIsSending}`, false);
  }

  async streamingResponseGenerator({ endpoint, data }) {
    const controller = new AbortController();
    const options = {
      timeout: 60000,
      signal: controller.signal,
    };

    const response = await this.makeStreamRequest(endpoint, data, controller);
    if (!response || controller.signal.aborted) return null;

    return {
      stream: this.createStreamGenerator(response),
      controller: controller,
    };
  }

  async makeStreamRequest(endpoint, data, controller) {
    this.store.commit(`copilot/${ADD_ACTIVE_REQUEST_CONTROLLER}`, controller);
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(
        `${response.status} HTTP Error during request to: ${endpoint}`,
      );
    }

    return response;
  }

  createStreamGenerator = async function* (response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const SSE_PREFIX = 'data: ';
    let buffer = ''; // Add buffer for incomplete messages

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        buffer += text; // Add new text to buffer

        // Split on double newlines and process each complete message
        const messages = buffer.split('\n\n');
        // Keep the last potentially incomplete message in the buffer
        buffer = messages.pop() || '';

        for (const message of messages) {
          const trimmedMessage = message?.trim();
          if (!trimmedMessage?.startsWith(SSE_PREFIX)) {
            continue;
          }

          try {
            const jsonStr = trimmedMessage.slice(SSE_PREFIX.length);
            const chunk = JSON.parse(jsonStr);

            if (chunk.content) {
              yield {
                content: chunk.content,
                done: false,
                response: null,
              };
            }

            if (chunk.done) {
              yield {
                done: true,
                response: chunk.response,
              };
              return; // Exit immediately when done
            }
          } catch (error) {
            console.warn(
              'Error parsing SSE message:',
              error,
              '\nMessage:',
              trimmedMessage,
            );
            continue; // Skip malformed messages
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  };

  createSimulatedGenerator = async function* (content) {
    yield {
      content,
      done: false,
      response: null,
    };

    yield {
      content: '',
      done: true,
      response: null,
    };
  };

  simulateStreamResponse(content, link = null, linkText = null) {
    return {
      stream: this.createSimulatedGenerator(content),
      actionName: 'simulated-stream',
      subclassification: '',
      ...(link && { link }),
      ...(linkText && { linkText }),
    };
  }
}
